import { useReducer } from "react";
import { useSyncFiltersToUrl } from "../useSyncStateToUrl";
import * as yup from "yup";
import type { BaseFilter } from "./types";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParamsV2";

const paramsSchema = yup.object().shape({
  page: yup.number().integer().min(1).nullable(),
  filters: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().defined(),
        key: yup.string().defined(),
        filter_type: yup.string(),
        value: yup.mixed(),
        profile_field_id: yup.number(),
        profile_field_type: yup.string(),
      }),
    )
    .nullable()
    .ensure(),
  /* Manage Members */
  order: yup.object(),
  /* Member Directory */
  sort: yup.string().oneOf(["oldest", "latest", "alphabetical"]).nullable(),
});

interface FilterState {
  filters: BaseFilter[];
  order?: Record<string, unknown>;
  page?: number | null;
  sort?: "oldest" | "latest" | "alphabetical" | null;
}

type FilterAction =
  | { type: "SET_FILTER"; payload: { id: string; filter: BaseFilter } }
  | { type: "CLEAR_FILTER"; payload: { id: string } }
  | { type: "CLEAR_FILTER_BY_KEY"; payload: { key: string } }
  | { type: "CLEAR_FILTERS" };

const removeSearchKeys = (state: FilterState) => ({
  ...state,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we are removing search_key from the state filters
  filters: state.filters.map(({ search_key, ...filter }) => ({ ...filter })),
});

const filterReducer = (
  state: FilterState,
  action: FilterAction,
): FilterState => {
  const filters = Array.isArray(state.filters) ? state.filters : [];

  switch (action.type) {
    case "SET_FILTER": {
      const { id, filter } = action.payload;
      const filtersForKey = filters.filter(
        (f: BaseFilter) => f.key === filter.key,
      );
      const hasIdAlready = filtersForKey.some((f: BaseFilter) => f.id === id);

      let nextFilters;
      if (hasIdAlready) {
        nextFilters = filters.map((f: BaseFilter) =>
          f.id === id ? filter : f,
        );
      } else {
        nextFilters = [...filters, filter];
      }

      return {
        ...state,
        filters: nextFilters,
      };
    }
    case "CLEAR_FILTER": {
      const nextFilters = filters.filter(
        (f: BaseFilter) => f.id !== action.payload.id,
      );
      return {
        ...state,
        filters: nextFilters,
      };
    }
    case "CLEAR_FILTER_BY_KEY": {
      const nextFilters = filters.filter(
        (f: BaseFilter) => f.key !== action.payload.key,
      );
      return {
        ...state,
        filters: nextFilters,
      };
    }

    case "CLEAR_FILTERS": {
      return {
        ...state,
        filters: [],
      };
    }
    default:
      return state;
  }
};

export const useContactFilter = () => {
  const queryParams = useRouterQueryParams(paramsSchema);
  const [state, dispatch] = useReducer(
    filterReducer,
    queryParams as unknown as FilterState,
  );

  useSyncFiltersToUrl(removeSearchKeys(state));

  const filters = Array.isArray(state.filters) ? state.filters : [];

  const filterParamsForKey = (key: string) =>
    filters.filter((filter: BaseFilter) => filter.key === key);

  const filterParamForKey = (key: string) =>
    filters.find((filter: BaseFilter) => filter.key === key);

  const setFilter = (id: string, newFilterParam: BaseFilter) => {
    const action = {
      type: "SET_FILTER" as const,
      payload: { id, filter: newFilterParam },
    };
    dispatch(action);
  };

  const clearFilter = (id: string) => {
    const action = { type: "CLEAR_FILTER" as const, payload: { id } };
    dispatch(action);
  };

  const clearFilterByKey = (key: string) => {
    const action = { type: "CLEAR_FILTER_BY_KEY" as const, payload: { key } };
    dispatch(action);
  };

  const clearFilters = () => {
    const action = { type: "CLEAR_FILTERS" as const };
    dispatch(action);
  };

  return {
    clearFilter,
    clearFilterByKey,
    clearFilters,
    filterParamForKey,
    filterParamsForKey,
    setFilter,
    filterParams: filters,
  };
};
